import { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, InputGroup, Row, Col } from 'react-bootstrap';
import CourseService from '../../../../services/CourseService';
import AlertService from '../../../../services/AlertService';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Dropdown } from 'primereact/dropdown';

const MapNewUserModal = ({ show, handleClose, users, authContext, onSaved, editData }) => {
  const initialFormData = {
    courseUser: '',
    tokenId: '',
    userName: '',
    token: '',
    password: '',
    allottedTime: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const submitControllerRef = useRef(null);

  useEffect(() => {
    if (editData) {
      setFormData({
        courseUser: editData.courseUserId != null ? Number(editData.courseUserId) : '',
        tokenId: editData.sessionTokenId || '',
        userName: editData.userName || '',
        token: editData.sessionToken || '',
        password: editData.password || '',
        allottedTime: editData.allottedHours || ''
      });
    } else {
      setFormData(initialFormData);
    }
    // eslint-disable-next-line
  }, [editData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Component unmount
  useEffect(() => {
    return () => {
      submitControllerRef.current?.abort();
    };
  }, []);

  const handleSubmit = async () => {
    if (submitControllerRef.current) {
      submitControllerRef.current.abort();
    }
    const controller = new AbortController();
    submitControllerRef.current = controller;

    setLoading(true);

    const payload = {
      id: editData?.id || 0,
      courseUserId: formData.courseUser,
      sessionTokenId: formData.tokenId,
      userName: formData.userName,
      sessionToken: formData.token,
      password: formData.password,
      allottedHours: Number(formData.allottedTime),
      displayName: '',
      type: 0
    };

    try {
      const result = await CourseService.SaveUserVM(
        payload,
        authContext,
        controller.signal
      );

      if (controller.signal.aborted) return;

      if (result.isSuccess) {
        AlertService.success(editData ? "User VM updated successfully!" : "User VM mapped successfully!");
        setFormData(initialFormData);
        handleClose();
        if (onSaved) onSaved();
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      AlertService.error("Network error");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const onClose = () => {
    setFormData(initialFormData);
    handleClose();
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static" enforceFocus={false} centered size="xl" className='p-3'>
      <Modal.Header className='border-0 px-md-4' closeButton>
        <Modal.Title className="w-100 text-center">Map New User</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form className='px-md-3'>
          <Row className="mb-3">
            <Col>
              <Form.Label>Course User</Form.Label>
              <Dropdown
                value={formData.courseUser}
                options={users}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setFormData({ ...formData, courseUser: e.value })}
                filter
                placeholder="Select User"
                emptyMessage="No user found"
                emptyFilterMessage="No matching user found"
                className="w-100 custom-input select-dropdown custom-input-color"
                panelClassName="select-dropdown-panel"
                appendTo="self"
                disabled={loading}
              />
            </Col>
            <Col>
              <Form.Label>Token ID</Form.Label>
              <Form.Control
                type="text"
                name="tokenId"
                className='custom-input custom-input-color'
                value={formData.tokenId}
                onChange={handleChange}
                disabled={loading}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Label>User Name</Form.Label>
              <Form.Control
                type="text"
                name="userName"
                className='custom-input custom-input-color'
                value={formData.userName}
                onChange={handleChange}
                disabled={loading}
              />
            </Col>
            <Col>
              <Form.Label>Token</Form.Label>
              <Form.Control
                type="text"
                name="token"
                className='custom-input custom-input-color'
                value={formData.token}
                onChange={handleChange}
                disabled={loading}
              />
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Label className='mb-1'>Password</Form.Label>
              <InputGroup className="mb-3">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="custom-input border-end-0 custom-input-color"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <InputGroup.Text
                  className="bg-transparent border-start-0"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                </InputGroup.Text>
              </InputGroup>
            </Col>
            <Col>
              <Form.Label>Allotted Time</Form.Label>
              <Form.Control
                type="number"
                name="allottedTime"
                className='custom-input custom-input-color'
                value={formData.allottedTime}
                onChange={handleChange}
                disabled={loading}
              />
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer className='border-0 gap-3 mb-2 px-md-4'>
        <Button
          variant="outline-secondary"
          className="px-4 px-md-5 py-2"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button variant="primary" className='px-4 px-md-5 py-2' onClick={handleSubmit} disabled={loading}>
          {loading ? "Saving..." : (editData ? "Update" : "Submit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MapNewUserModal;